import React, { useState } from 'react';

import { Paper, Skeleton, Typography } from '@mui/material';
import { useMount } from 'react-use';
import type { WorkflowInboxWorkflow } from 'src/components/dashboard';
import {
  useGetIsInboxWorkflow,
  WorkflowActionsListBody,
  WorkflowInboxFilter,
} from 'src/components/dashboard';
import { Apollo } from 'src/gql/generated';
import { useGetWorkflowInboxWorkflow } from 'src/hooks';
import {
  DownloadWorkflowButton,
  RevertWorkflowToDraftButton,
  SendForSignatureButton,
} from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  body: {
    border: theme.appBorders.outline,
    borderRadius: 8,
    padding: theme.spacing(1, 0),
  },
  circle: {
    alignItems: 'center',
    backgroundColor: 'rgb(245, 246, 251)',
    borderRadius: '50%',
    display: 'flex',
    height: 80,
    justifyContent: 'center',
    width: 80,
  },
  content: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  loadingSkeleton: {
    borderRadius: 8,
  },
  paper: {
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: 'fit-content',
    // Ensures card/paper has same width for each grid column.
    minWidth: 0,
    padding: theme.spacing(2),
  },
}));

export function WorkflowWhatsNext({ workflowId }: { workflowId: UUID }): JSX.Element {
  const { classes } = useStyles();
  const { loading, workflow } = useGetWorkflowInboxWorkflow(workflowId);

  if (loading || !workflow)
    return (
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <Typography variant="subtitle1">{"What's next?"}</Typography>
          <Typography variant="body2">
            These actions need to be completed before sending for signature.
          </Typography>
        </div>
        <Skeleton
          className={classes.loadingSkeleton}
          height={120}
          variant="rectangular"
          width="100%"
        />
      </Paper>
    );

  return <WorkflowWhatsNextBody workflow={workflow} />;
}

export function WorkflowWhatsNextBody({ workflow }: { workflow: WorkflowInboxWorkflow }) {
  const { classes } = useStyles();
  const { getIsInboxWorkflow } = useGetIsInboxWorkflow();
  const [isInboxWorkflow, setIsInboxWorkflow] = useState<boolean>(false);

  // Ensures that WhatsNext only changes when the user changes tabs.
  useMount(() => {
    const { isInboxWorkflow } = getIsInboxWorkflow(workflow, WorkflowInboxFilter.All);
    setIsInboxWorkflow(isInboxWorkflow);
  });

  if (!isInboxWorkflow) return <NoActionsWorkflow workflow={workflow} />;

  return (
    <Paper className={classes.paper}>
      <WorkflowWhatsNextContent workflow={workflow} />
    </Paper>
  );
}

export function WorkflowWhatsNextContent({
  onClose,
  workflow,
}: {
  workflow: WorkflowInboxWorkflow;
  onClose?: () => void;
}) {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.header}>
        <Typography variant="subtitle1">Actions</Typography>
        <Typography variant="body2">
          These actions need to be completed before moving to the next workflow stage:
        </Typography>
      </div>
      <WorkflowActionsListBody className={classes.body} workflow={workflow} onClose={onClose} />
    </>
  );
}

export function NoActionsWorkflow({ workflow }: { workflow: WorkflowInboxWorkflow }): JSX.Element {
  const { classes } = useStyles();

  switch (workflow.state) {
    case Apollo.WorkflowState.Negotiating:
    case Apollo.WorkflowState.ReadyForSignature:
      return (
        <Paper className={classes.paper}>
          <Typography>The workflow is ready for signature:</Typography>
          <SendForSignatureButton
            buttonProps={{ fullWidth: true, variant: 'outlined' }}
            workflow={workflow}
          />
        </Paper>
      );
    case Apollo.WorkflowState.Signing:
      return (
        <Paper className={classes.paper}>
          <Typography>Documents are being signed:</Typography>
          <RevertWorkflowToDraftButton
            buttonProps={{ fullWidth: true, variant: 'outlined' }}
            workflow={workflow}
          />
        </Paper>
      );
    case Apollo.WorkflowState.Signed:
    case Apollo.WorkflowState.Complete:
    case Apollo.WorkflowState.Template:
      return (
        <Paper className={classes.paper}>
          <Typography>Download signed documents:</Typography>
          <DownloadWorkflowButton
            buttonProps={{ fullWidth: true, variant: 'outlined' }}
            workflow={workflow}
          />
        </Paper>
      );
  }
}
